import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Upload, MapPin, Phone, Mail, X, ArrowUp, ArrowDown, Save } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import RichTextEditor from './RichTextEditor';
import api from '../../utils/api';

const OfficeManagement = ({ onUpdate }) => {
  const [offices, setOffices] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [groupOrder, setGroupOrder] = useState([]);
  const [savingGroupOrder, setSavingGroupOrder] = useState(false);
  const [groupDescriptions, setGroupDescriptions] = useState({});
  const [savingGroupDesc, setSavingGroupDesc] = useState(false);
  const [addingNewGroup, setAddingNewGroup] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    fax: '',
    email: '',
    business_hours: '',
    image_url: '',
    is_active: true,
    group: '',
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchOffices();
  }, []);

  const fetchOffices = async () => {
    try {
      const response = await api.get('/offices');
      setOffices(response.data);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('事業内容の取得に失敗しました:', error);
    }
    try {
      const go = await api.get('/offices/group-order');
      setGroupOrder(go.data.order || []);
    } catch (error) {
      // 未設定時は空
    }
    try {
      const gd = await api.get('/offices/group-descriptions');
      setGroupDescriptions(gd.data.descriptions || {});
    } catch (error) {
      // 未設定時は空
    }
  };

  // 表示順を変更
  const handleOrderChange = async (officeName, newOrder) => {
    try {
      await api.put(`/offices/${officeName}/order`, { order: newOrder });
      toast({
        title: '成功',
        description: '表示順を更新しました',
      });
      fetchOffices();
    } catch (error) {
      toast({
        title: 'エラー',
        description: '表示順の更新に失敗しました',
        variant: 'destructive',
      });
    }
  };

  // 上に移動
  const moveUp = async (index) => {
    if (index === 0) return;
    const currentOffice = offices[index];
    const previousOffice = offices[index - 1];
    
    try {
      await api.put(`/offices/${currentOffice.name}/order`, { order: previousOffice.order });
      await api.put(`/offices/${previousOffice.name}/order`, { order: currentOffice.order });
      fetchOffices();
      toast({
        title: '成功',
        description: '表示順を入れ替えました',
      });
    } catch (error) {
      toast({
        title: 'エラー',
        description: '表示順の更新に失敗しました',
        variant: 'destructive',
      });
    }
  };

  // 下に移動
  const moveDown = async (index) => {
    if (index === offices.length - 1) return;
    const currentOffice = offices[index];
    const nextOffice = offices[index + 1];
    
    try {
      await api.put(`/offices/${currentOffice.name}/order`, { order: nextOffice.order });
      await api.put(`/offices/${nextOffice.name}/order`, { order: currentOffice.order });
      fetchOffices();
      toast({
        title: '成功',
        description: '表示順を入れ替えました',
      });
    } catch (error) {
      toast({
        title: 'エラー',
        description: '表示順の更新に失敗しました',
        variant: 'destructive',
      });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: 'エラー',
          description: 'ファイルサイズは2MB以下にしてください',
          variant: 'destructive',
        });
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast({
          title: 'エラー',
          description: '画像ファイルを選択してください',
          variant: 'destructive',
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImagePreview(base64String);
        setFormData({ ...formData, image_url: base64String });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingOffice) {
        await api.put(`/offices/${editingOffice.id}`, formData);
        toast({
          title: '更新完了',
          description: '事業内容を更新しました',
        });
      } else {
        await api.post('/offices', formData);
        toast({
          title: '作成完了',
          description: '新しい事業内容を作成しました',
        });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchOffices();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.response?.data?.detail || '操作に失敗しました',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingOffice(item);
    setFormData({
      name: item.name,
      description: item.description,
      address: item.address,
      phone: item.phone,
      fax: item.fax || '',
      email: item.email || '',
      business_hours: item.business_hours,
      image_url: item.image_url || '',
      is_active: item.is_active,
      group: item.group || '',
    });
    setImagePreview(item.image_url || null);
    setAddingNewGroup(false);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) {
      return;
    }

    try {
      await api.delete(`/offices/${id}`);
      toast({
        title: '削除完了',
        description: '事業内容を削除しました',
      });
      fetchOffices();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.response?.data?.detail || '削除に失敗しました',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      address: '',
      phone: '',
      fax: '',
      email: '',
      business_hours: '',
      image_url: '',
      is_active: true,
      group: '',
    });
    setImagePreview(null);
    setEditingOffice(null);
    setAddingNewGroup(false);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const existingGroups = Array.from(
    new Set(offices.map((o) => (o.group || '').trim()).filter(Boolean))
  );
  const orderedGroups = [
    ...groupOrder.filter((g) => existingGroups.includes(g)),
    ...existingGroups.filter((g) => !groupOrder.includes(g)),
  ];

  const moveGroup = (index, dir) => {
    const arr = [...orderedGroups];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    setGroupOrder(arr);
  };

  const saveGroupOrder = async () => {
    setSavingGroupOrder(true);
    try {
      await api.put('/offices/group-order', { order: orderedGroups });
      toast({ title: '保存しました', description: 'グループの表示順を更新しました' });
      if (onUpdate) onUpdate();
    } catch (error) {
      toast({ title: 'エラー', description: '表示順の保存に失敗しました', variant: 'destructive' });
    } finally {
      setSavingGroupOrder(false);
    }
  };

  const saveGroupDescriptions = async () => {
    setSavingGroupDesc(true);
    try {
      await api.put('/offices/group-descriptions', { descriptions: groupDescriptions });
      toast({ title: '保存しました', description: 'グループの説明文を更新しました' });
      if (onUpdate) onUpdate();
    } catch (error) {
      toast({ title: 'エラー', description: '説明文の保存に失敗しました', variant: 'destructive' });
    } finally {
      setSavingGroupDesc(false);
    }
  };

  const getSections = (g) => (Array.isArray(groupDescriptions[g]) ? groupDescriptions[g] : []);
  const addSection = (g) => {
    setGroupDescriptions({ ...groupDescriptions, [g]: [...getSections(g), { title: '', body: '' }] });
  };
  const removeSection = (g, idx) => {
    setGroupDescriptions({ ...groupDescriptions, [g]: getSections(g).filter((_, i) => i !== idx) });
  };
  const updateSection = (g, idx, field, val) => {
    const next = getSections(g).map((s, i) => (i === idx ? { ...s, [field]: val } : s));
    setGroupDescriptions({ ...groupDescriptions, [g]: next });
  };

  return (
    <Card className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border-amber-500/30 backdrop-blur-sm">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <CardTitle className="text-xl sm:text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
            事業内容管理
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            {!isEditMode && (
              <Button 
                onClick={() => setIsEditMode(true)}
                variant="outline"
                className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
              >
                <Edit className="w-4 h-4 mr-2" />
                編集モード
              </Button>
            )}
            {isEditMode && (
              <Button 
                onClick={() => setIsEditMode(false)}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <X className="w-4 h-4 mr-2" />
                編集終了
              </Button>
            )}
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetForm}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/30"
              >
                <Plus className="w-4 h-4 mr-2" />
                新規作成
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 border-amber-500/30 text-gray-900">
              <DialogHeader>
                <DialogTitle className="text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                  {editingOffice ? '事業内容を編集' : '新しい事業内容を作成'}
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  {editingOffice ? '事業内容の内容を編集します' : '新しい事業内容を作成します'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* 画像アップロード */}
                <div className="space-y-4">
                  <Label className="text-gray-600">施設画像</Label>
                  <div className="flex items-center space-x-4">
                    {imagePreview && (
                      <div className="w-32 h-32 border-2 border-amber-500/30 rounded-lg overflow-hidden bg-white/10">
                        <img 
                          src={imagePreview} 
                          alt="Office" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <Label 
                        htmlFor="image-upload" 
                        className="cursor-pointer inline-flex items-center px-4 py-2 bg-white/50 border border-amber-500/30 rounded-lg hover:bg-amber-500/10 transition-all"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        画像をアップロード
                      </Label>
                      <Input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        推奨: PNG、JPG形式、最大2MB
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-600">事業所名</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="例: 本社"
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="group" className="text-gray-600">グループ（サービス種別）</Label>
                  {addingNewGroup ? (
                    <div className="flex gap-2">
                      <Input
                        id="group"
                        value={formData.group}
                        onChange={(e) => setFormData({ ...formData, group: e.target.value })}
                        placeholder="新しいグループ名を入力"
                        className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                        data-testid="office-group-input"
                        autoFocus
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => { setAddingNewGroup(false); setFormData({ ...formData, group: '' }); }}
                        className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10 whitespace-nowrap"
                      >
                        一覧から選ぶ
                      </Button>
                    </div>
                  ) : (
                    <Select
                      value={formData.group ? formData.group : '__none__'}
                      onValueChange={(val) => {
                        if (val === '__new__') { setAddingNewGroup(true); setFormData({ ...formData, group: '' }); }
                        else if (val === '__none__') { setFormData({ ...formData, group: '' }); }
                        else { setFormData({ ...formData, group: val }); }
                      }}
                    >
                      <SelectTrigger data-testid="office-group-select" className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500">
                        <SelectValue placeholder="グループを選択" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">未分類（グループなし）</SelectItem>
                        {existingGroups.map((g) => (
                          <SelectItem key={g} value={g}>{g}</SelectItem>
                        ))}
                        <SelectItem value="__new__">＋ 新しいグループを追加…</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <p className="text-xs text-gray-500">同じグループの事業所は、ヘッダーの「事業内容」メニューでまとめて表示されます</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-gray-600">説明</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="text-gray-600">住所</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="〒000-0000 都道府県市区町村..."
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-600">電話番号</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="000-0000-0000"
                      className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fax" className="text-gray-600">FAX</Label>
                    <Input
                      id="fax"
                      value={formData.fax}
                      onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                      placeholder="000-0000-0000"
                      className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-600">メールアドレス</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="info@example.com"
                      className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_hours" className="text-gray-600">営業時間</Label>
                    <Input
                      id="business_hours"
                      value={formData.business_hours}
                      onChange={(e) => setFormData({ ...formData, business_hours: e.target.value })}
                      placeholder="平日 9:00～18:00"
                      className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active" className="text-gray-600">公開する</Label>
                </div>

                <div className="flex justify-end space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleDialogClose}
                    className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                  >
                    キャンセル
                  </Button>
                  <Button 
                    type="submit"
                    className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
                    disabled={loading}
                  >
                    {loading ? '処理中...' : (editingOffice ? '更新' : '作成')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {orderedGroups.length > 0 && (
          <div className="mb-6 p-4 rounded-lg border border-amber-500/20 bg-white/40" data-testid="group-order-panel">
            <div className="flex items-center justify-between mb-3 gap-2">
              <h3 className="text-sm font-semibold text-gray-700">グループの表示順（事業内容メニュー）</h3>
              <Button type="button" size="sm" onClick={saveGroupOrder} disabled={savingGroupOrder}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700" data-testid="save-group-order-btn">
                <Save className="w-4 h-4 mr-1" />{savingGroupOrder ? '保存中...' : '順番を保存'}
              </Button>
            </div>
            <ul className="space-y-2">
              {orderedGroups.map((g, idx) => (
                <li key={g} className="flex items-center justify-between gap-2 px-3 py-2 rounded bg-amber-50/60 border border-amber-500/20" data-testid={`group-order-item-${g}`}>
                  <span className="text-sm text-amber-700 font-medium">{idx + 1}. {g}</span>
                  <div className="flex gap-1">
                    <Button type="button" variant="outline" size="icon" onClick={() => moveGroup(idx, -1)} disabled={idx === 0}
                      className="h-7 w-7 border-amber-500/30 text-amber-600 hover:bg-amber-500/10" data-testid={`group-move-up-${g}`}>
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="outline" size="icon" onClick={() => moveGroup(idx, 1)} disabled={idx === orderedGroups.length - 1}
                      className="h-7 w-7 border-amber-500/30 text-amber-600 hover:bg-amber-500/10" data-testid={`group-move-down-${g}`}>
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 mt-2">※ 未分類（本社など）は常に先頭に表示されます</p>
          </div>
        )}
        {orderedGroups.length > 0 && (
          <div className="mb-6 p-4 rounded-lg border border-amber-500/20 bg-white/40" data-testid="group-desc-panel">
            <div className="flex items-center justify-between mb-3 gap-2">
              <h3 className="text-sm font-semibold text-gray-700">グループの説明文（グループページに表示）</h3>
              <Button type="button" size="sm" onClick={saveGroupDescriptions} disabled={savingGroupDesc}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700" data-testid="save-group-desc-btn">
                <Save className="w-4 h-4 mr-1" />{savingGroupDesc ? '保存中...' : '説明を保存'}
              </Button>
            </div>
            <ul className="space-y-4">
              {orderedGroups.map((g) => {
                const sections = getSections(g);
                return (
                  <li key={g} className="px-3 py-3 rounded bg-amber-50/60 border border-amber-500/20" data-testid={`group-desc-item-${g}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-amber-700 font-medium">{g}</span>
                      <Button type="button" size="sm" variant="outline" onClick={() => addSection(g)}
                        className="border-amber-500/30 text-amber-600 hover:bg-amber-500/10" data-testid={`group-add-section-${g}`}>
                        <Plus className="w-4 h-4 mr-1" />セクションを追加
                      </Button>
                    </div>
                    <div className="mt-3 space-y-4">
                      {sections.length === 0 && (
                        <p className="text-xs text-gray-500">「セクションを追加」でタイトルと説明を追加できます</p>
                      )}
                      {sections.map((sec, idx) => (
                        <div key={idx} className="rounded-lg border border-amber-500/20 bg-white/60 p-3 space-y-2" data-testid={`group-section-${g}-${idx}`}>
                          <div className="flex items-center gap-2">
                            <Input
                              value={sec.title || ''}
                              onChange={(e) => updateSection(g, idx, 'title', e.target.value)}
                              placeholder="タイトル（例: サービス概要）"
                              className="bg-white/70 border-amber-500/30 text-gray-900 focus:border-amber-500"
                              data-testid={`group-section-title-${g}-${idx}`}
                            />
                            <Button type="button" variant="outline" size="icon" onClick={() => removeSection(g, idx)}
                              className="h-9 w-9 flex-shrink-0 border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`group-section-remove-${g}-${idx}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <RichTextEditor
                            value={sec.body || ''}
                            onChange={(html) => updateSection(g, idx, 'body', html)}
                            placeholder="説明を入力してください"
                            testId={`group-section-editor-${g}-${idx}`}
                          />
                        </div>
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-gray-500 mt-2">※ 入力した説明は、事業内容メニューから開くグループページのタイトル下に表示されます</p>
          </div>
        )}
        <div className="space-y-4">
          {offices.map((office) => (
            <div
              key={office.id}
              className="border border-amber-500/20 rounded-lg p-6 hover:border-amber-500/50 bg-white/30 transition-all backdrop-blur-sm"
            >
              <div className="flex gap-6">
                {office.image_url && (
                  <div className="w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden border border-amber-500/30">
                    <img 
                      src={office.image_url} 
                      alt={office.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500 font-mono">#{office.order}</span>
                      <h3 className="text-xl font-semibold text-gray-900">{office.name}</h3>
                      {office.group && (
                        <span className="text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-600 border border-amber-500/30" data-testid={`office-group-badge-${office.id}`}>
                          {office.group}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 rounded ${office.is_active ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-gray-100/20 text-gray-600 border border-gray-300/30'}`}>
                        {office.is_active ? '公開中' : '非公開'}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => moveUp(offices.indexOf(office))}
                        disabled={offices.indexOf(office) === 0}
                        className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10 disabled:opacity-30"
                        title="上に移動"
                        data-testid={`office-move-up-${office.id}`}
                      >
                        ↑
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => moveDown(offices.indexOf(office))}
                        disabled={offices.indexOf(office) === offices.length - 1}
                        className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10 disabled:opacity-30"
                        title="下に移動"
                        data-testid={`office-move-down-${office.id}`}
                      >
                        ↓
                      </Button>
                      {isEditMode && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleEdit(office)}
                            className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleDelete(office.id)}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{office.description}</p>
                  
                  <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-amber-500" />
                      <span>{office.address}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-amber-500" />
                      <span>{office.phone}</span>
                    </div>
                    {office.email && (
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-amber-500" />
                        <span>{office.email}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <span className="text-amber-500">🕐</span>
                      <span>{office.business_hours}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default OfficeManagement;
